import { ReactNode, RefObject, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  addDays,
  format,
  startOfDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
} from 'date-fns';
import { cn } from '@/lib/utils';
import { getDateLocale } from '@/utils/date';
import {
  layoutEvents,
  type Interval,
  type LaidOutEvent,
} from '@/lib/calendar/overlap';
import {
  daysInWeek,
  labelForView,
  shiftAnchor,
  isToday as isTodayDate,
} from '@/lib/calendar/range';
import type {
  CalendarView,
  CalendarEvent,
  Staff,
  Column,
  WorkingHours,
  EventStatus,
  CalendarLayer,
  CalendarLayerVariant,
  CalendarLayerInterval,
} from '@/lib/calendar/types';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarInitials } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

/*
 * Calendar — DESIGN_SYSTEM.md §2.2
 * Unified component covering day | week | month | team views. Same header,
 * same event-chip vocabulary, same status colors. Body layout differs by
 * view; day/week/team share a column-based renderer. Month rendered separately.
 *
 * The rendering invariant: events MUST NEVER visually overlap. lib/calendar/
 * overlap.layoutEvents resolves any overlap into per-cluster lanes.
 */

// ── Public API ─────────────────────────────────────────────────────────

export interface CalendarProps {
  view: CalendarView;
  date: Date;
  onViewChange?: (v: CalendarView) => void;
  onDateChange?: (d: Date) => void;
  events: CalendarEvent[];
  /** Required when view === 'team'. */
  staff?: Staff[];
  workingHours?: WorkingHours;
  /** Grid line every N minutes; default 60. */
  slotMinutes?: number;
  /** Pixels per minute; default 1 (= 60px/hour). */
  pixelsPerMinute?: number;
  weekStartsOn?: 0 | 1;
  onEventClick?: (e: CalendarEvent) => void;
  onSlotClick?: (slot: { start: Date; columnId: string }) => void;
  /** Backdrop layers (availability, busy, overrides). Day/week/team views only. */
  layers?: CalendarLayer[];
  loading?: boolean;
  emptyState?: ReactNode;
  /** Right-side toolbar slot in header (filters, +New action). */
  toolbar?: ReactNode;
  /** Hide the view-switcher tabs (e.g. when locked to a single view). */
  hideViewTabs?: boolean;
  /** Hide the entire header (nav arrows + Today + date label + tabs + toolbar).
   *  For consumers that own their own page-level date navigation. */
  hideHeader?: boolean;
  /** When true, the body fills the parent's available height (parent must
   *  have a bounded height) and scrolls internally. Default: fixed 14h
   *  viewport height. */
  bodyFill?: boolean;
  className?: string;
}

const DEFAULT_HOURS: WorkingHours = { start: 8, end: 20 };
const DEFAULT_PX_PER_MIN = 1;

// ── Layer variant styles ───────────────────────────────────────────────

interface LayerVariantStyle {
  /** Class applied to the absolute-positioned interval block. */
  block: string;
  /** Class for the optional label inside the block. */
  label: string;
  /** When true, intervals are non-interactive and don't capture pointer events. */
  passthrough?: boolean;
}

// Diagonal stripe pattern via repeating-linear-gradient — encoded as inline
// arbitrary value because Tailwind can't express it as a utility.
const STRIPE_BUSY = '[background:repeating-linear-gradient(45deg,var(--surface-2)_0_6px,var(--line)_6px_12px)]';
const STRIPE_DANGER = '[background:repeating-linear-gradient(45deg,var(--danger-soft)_0_6px,transparent_6px_12px)]';

const layerVariantStyles: Record<CalendarLayerVariant, LayerVariantStyle> = {
  available:           { block: 'bg-success-soft shadow-md shadow-success/30',                        label: 'text-success-text' },
  busy:                { block: cn(STRIPE_BUSY, 'border border-line/60 opacity-80'),               label: 'text-soft' },
  'override-available':{ block: 'bg-success-soft border border-success',                            label: 'text-success-text' },
  'override-blocked':  { block: cn(STRIPE_DANGER, 'border border-dashed border-danger/60'),        label: 'text-danger-text' },
  'group-class':       { block: 'bg-violet-50 border border-violet-200 border-l-4 !border-l-violet-500', label: 'text-violet-700' },
};

// ── Status color map ───────────────────────────────────────────────────

const statusStyles: Record<EventStatus, { border: string; stripe: string; bg: string; text: string }> = {
  confirmed:    { border: 'border-status-booked',   stripe: 'bg-status-booked',   bg: 'bg-brand-50',   text: 'text-brand-700' },
  pending:      { border: 'border-warn',            stripe: 'bg-warn',            bg: 'bg-warn-soft',  text: 'text-warn-text' },
  'in-progress':{ border: 'border-brand-500',      stripe: 'bg-brand-500',      bg: 'bg-brand-100',  text: 'text-brand-700' },
  done:         { border: 'border-success',         stripe: 'bg-success',         bg: 'bg-success-soft', text: 'text-success-text' },
  cancelled:    { border: 'border-line-strong',     stripe: 'bg-line-strong',     bg: 'bg-surface-2',  text: 'text-soft' },
  blocked:      { border: 'border-status-blocked',  stripe: 'bg-status-blocked',  bg: 'bg-surface-2',  text: 'text-soft' },
  'group-class':{ border: 'border-violet-200',      stripe: 'bg-violet-500',      bg: 'bg-violet-50',  text: 'text-violet-700' },
};

// ── Header ─────────────────────────────────────────────────────────────

interface HeaderProps {
  view: CalendarView;
  date: Date;
  onViewChange?: (v: CalendarView) => void;
  onDateChange?: (d: Date) => void;
  events: CalendarEvent[];
  toolbar?: ReactNode;
  hideViewTabs?: boolean;
  weekStartsOn?: 0 | 1;
}

function CalendarHeader({
  view,
  date,
  onViewChange,
  onDateChange,
  events,
  toolbar,
  hideViewTabs,
  weekStartsOn = 1,
}: HeaderProps) {
  const { t } = useTranslation('common');
  const goPrev = () => onDateChange?.(shiftAnchor(view, date, -1));
  const goNext = () => onDateChange?.(shiftAnchor(view, date, 1));
  const goToday = () => onDateChange?.(new Date());

  const counts = useMemo(() => {
    const c = { confirmed: 0, pending: 0, walkin: 0 };
    for (const e of events) {
      if (e.status === 'confirmed') c.confirmed++;
      else if (e.status === 'pending') c.pending++;
    }
    return c;
  }, [events]);

  return (
    <div className="flex flex-col gap-3 border-b border-line bg-surface px-4 py-3 sm:px-5 sm:py-3.5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <IconButton
            icon={<ChevronLeft />}
            aria-label={t('pagination.previous')}
            variant="subtle"
            size="md"
            onClick={goPrev}
          />
          <Button variant="ghost" size="sm" onClick={goToday}>
            {t('time.today')}
          </Button>
          <IconButton
            icon={<ChevronRight />}
            aria-label={t('pagination.next')}
            variant="subtle"
            size="md"
            onClick={goNext}
          />
        </div>

        <h2 className="text-base sm:text-h3 font-semibold text-ink truncate flex-1 min-w-0">
          <span className="sm:hidden">{labelForView(view, date, undefined, { compact: true })}</span>
          <span className="hidden sm:inline">{labelForView(view, date)}</span>
        </h2>

        {!hideViewTabs && onViewChange && (
          <div className="hidden sm:block">
            <Tabs value={view} onValueChange={(v) => onViewChange(v as CalendarView)}>
              <TabsList>
                <TabsTrigger value="day">{t('calendarViews.day')}</TabsTrigger>
                <TabsTrigger value="week">{t('calendarViews.week')}</TabsTrigger>
                <TabsTrigger value="month">{t('calendarViews.month')}</TabsTrigger>
                <TabsTrigger value="team">{t('calendarViews.team')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}
        {/* Mobile view switcher: select. Native to keep bundle tight. */}
        {!hideViewTabs && onViewChange && (
          <select
            className="sm:hidden h-8 rounded-md border border-line bg-surface px-2 text-sm"
            value={view}
            onChange={(e) => onViewChange(e.target.value as CalendarView)}
            aria-label={t('calendarViews.selectView')}
          >
            <option value="day">{t('calendarViews.day')}</option>
            <option value="week">{t('calendarViews.week')}</option>
            <option value="month">{t('calendarViews.month')}</option>
            <option value="team">{t('calendarViews.team')}</option>
          </select>
        )}

        {toolbar && <div className="flex items-center gap-2">{toolbar}</div>}
      </div>

      {(counts.confirmed > 0 || counts.pending > 0) && (
        <div className="flex items-center gap-3 text-body-xs text-soft" data-numeric>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-pill bg-status-booked" />
            {counts.confirmed} {t('status.confirmed')}
          </span>
          {counts.pending > 0 && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-pill bg-warn" />
                {counts.pending} {t('status.pending')}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Body: day / team / week (column-based) ─────────────────────────────

interface ColumnsBodyProps {
  columns: Column[];
  events: CalendarEvent[];
  layers?: CalendarLayer[];
  workingHours: WorkingHours;
  slotMinutes: number;
  pixelsPerMinute: number;
  showAvatars?: boolean;
  loading?: boolean;
  emptyState?: ReactNode;
  bodyFill?: boolean;
  onEventClick?: (e: CalendarEvent) => void;
  onSlotClick?: (slot: { start: Date; columnId: string }) => void;
  /** When provided, used as the date base for "now" indicator + slot clicks. */
  dateForColumn?: (column: Column) => Date;
  /** Header strip to keep in sync while the body scrolls horizontally. */
  headerRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Floor for a day/staff column. Below this, event chips wrap to one character
 * per line (7 columns on a phone leave ~33px each), so the body scrolls
 * horizontally instead of squeezing. Wide viewports never hit the floor.
 */
const COLUMN_MIN_WIDTH = 96;

function ColumnsBody({
  columns,
  events,
  layers,
  workingHours,
  slotMinutes,
  pixelsPerMinute,
  showAvatars,
  loading,
  emptyState,
  bodyFill = false,
  onEventClick,
  onSlotClick,
  dateForColumn,
  headerRef,
}: ColumnsBodyProps) {
  // Always render full 24h grid so events outside working hours stay visible.
  // workingHours is used as the initial scroll target + "now" indicator window.
  const DAY_START_HOUR = 0;
  const DAY_END_HOUR = 24;
  const dayMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const totalHeight = dayMinutes * pixelsPerMinute;
  const hourCount = DAY_END_HOUR - DAY_START_HOUR;
  const slotPx = slotMinutes * pixelsPerMinute;
  // Default visible viewport ≈ 14h window. Inner grid is full 24h — scroll reveals rest.
  const VIEWPORT_HOURS = 14;
  const viewportHeight = VIEWPORT_HOURS * 60 * pixelsPerMinute;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);

  // Default scroll lands at 6:30 so morning is in view; user can scroll for rest.
  const DEFAULT_SCROLL_MINS = 6 * 60 + 30;

  // Reset the "did scroll" guard whenever the column set changes (view switch).
  useLayoutEffect(() => {
    didInitialScrollRef.current = false;
  }, [columns, loading]);

  // Watch the scroller — once it's tall enough to actually scroll to `top`,
  // do it exactly once. ResizeObserver covers the mount-time race where layout
  // isn't flushed when a normal effect fires (e.g. list→day first switch).
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const top = Math.max(0, DEFAULT_SCROLL_MINS * pixelsPerMinute);

    const attempt = () => {
      if (didInitialScrollRef.current) return;
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 0) return;
      el.scrollTo({ top: Math.min(top, max), behavior: 'auto' });
      didInitialScrollRef.current = true;
    };

    attempt();
    if (didInitialScrollRef.current) return;

    const ro = new ResizeObserver(attempt);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [columns, pixelsPerMinute, loading]);

  // Group events by columnId.
  const eventsByColumn = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const c of columns) map.set(c.id, []);
    for (const ev of events) {
      const cid = ev.columnId ?? columns[0]?.id;
      if (cid && map.has(cid)) map.get(cid)!.push(ev);
    }
    return map;
  }, [columns, events]);

  // Group layers' intervals by columnId, preserving layer order (lower z first).
  // Each entry pairs the interval with its source layer for styling/click handling.
  type LayerHit = { layer: CalendarLayer; interval: CalendarLayerInterval };
  const layerHitsByColumn = useMemo(() => {
    const map = new Map<string, LayerHit[]>();
    for (const c of columns) map.set(c.id, []);
    if (!layers || layers.length === 0) return map;
    const sorted = [...layers].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
    for (const layer of sorted) {
      for (const interval of layer.intervals) {
        const arr = map.get(interval.columnId);
        if (arr) arr.push({ layer, interval });
      }
    }
    return map;
  }, [columns, layers]);

  if (!loading && events.length === 0 && emptyState) {
    return <div className="flex min-h-[320px] items-center justify-center">{emptyState}</div>;
  }

  return (
    <div
      ref={scrollerRef}
      className={cn(
        'relative bg-surface overflow-auto',
        bodyFill && 'flex-1 min-h-0',
      )}
      style={bodyFill ? undefined : { height: viewportHeight }}
      onScroll={(e) => {
        // Drag the column header along with the body's horizontal scroll.
        if (headerRef?.current) headerRef.current.scrollLeft = e.currentTarget.scrollLeft;
      }}
    >
      <div
        className="relative grid"
        style={{
          gridTemplateColumns: `60px repeat(${columns.length}, minmax(${COLUMN_MIN_WIDTH}px,1fr))`,
          minHeight: totalHeight,
        }}
      >
        {/* Hour gutter — pinned left so times stay readable while scrolling x */}
        <div className="sticky left-0 z-20 border-r border-line bg-surface">
          {Array.from({ length: hourCount }, (_, i) => {
            const hour = DAY_START_HOUR + i;
            return (
              <div
                key={hour}
                className="absolute left-0 right-0 -translate-y-1/2 px-2 text-right text-mono-eyebrow text-soft"
                style={{ top: i * 60 * pixelsPerMinute }}
                data-numeric
              >
                {format(new Date(2000, 0, 1, hour, 0), 'HH:mm')}
              </div>
            );
          })}
        </div>

        {/* Columns */}
        {columns.map((col) => {
          const colEvents = eventsByColumn.get(col.id) ?? [];
          type Item = Interval & { ref: CalendarEvent };
          const items: Item[] = colEvents.map((e) => ({
            start: e.start.getTime(),
            end: e.end.getTime(),
            ref: e,
          }));
          const laid = layoutEvents<Item>(items);
          const colDate = dateForColumn?.(col);
          const isCurrentColumn = col.isToday;
          const now = new Date();
          const showNow = isCurrentColumn;
          const nowTop = showNow
            ? (now.getHours() * 60 + now.getMinutes()) * pixelsPerMinute
            : 0;

          return (
            <div
              key={col.id}
              data-testid="calendar-day-column"
              className="relative border-r border-line last:border-r-0"
              onClick={(e) => {
                if (!onSlotClick || !colDate) return;
                const target = e.currentTarget;
                const rect = target.getBoundingClientRect();
                const y = e.clientY - rect.top + target.scrollTop;
                const minsFromStart = Math.floor(y / pixelsPerMinute / slotMinutes) * slotMinutes;
                const startDate = new Date(colDate);
                startDate.setHours(DAY_START_HOUR, 0, 0, 0);
                startDate.setMinutes(startDate.getMinutes() + minsFromStart);
                onSlotClick({ start: startDate, columnId: col.id });
              }}
            >
              {/* Hour grid lines */}
              {Array.from({ length: hourCount }, (_, i) => (
                <div
                  key={i}
                  className="absolute inset-x-0 border-t border-line"
                  style={{ top: i * 60 * pixelsPerMinute }}
                  aria-hidden
                />
              ))}
              {/* Half-hour dashed lines (only when slotMinutes <= 30 in spec; we
                  show 30-min lines for day/team for visual density). */}
              {slotPx >= 30 && Array.from({ length: hourCount }, (_, i) => (
                <div
                  key={`half-${i}`}
                  className="absolute inset-x-0 border-t border-line/60"
                  style={{ top: (i * 60 + 30) * pixelsPerMinute, borderTopStyle: 'dashed' }}
                  aria-hidden
                />
              ))}

              {/* Layers (availability / busy / overrides) */}
              {(layerHitsByColumn.get(col.id) ?? []).map(({ layer, interval }) => {
                const startTotalMins = interval.start.getHours() * 60 + interval.start.getMinutes();
                const endTotalMins = interval.end.getHours() * 60 + interval.end.getMinutes();
                if (endTotalMins <= startTotalMins) return null;
                const top = startTotalMins * pixelsPerMinute;
                const height = (endTotalMins - startTotalMins) * pixelsPerMinute;
                const styles = layerVariantStyles[layer.variant];
                const interactive = !styles.passthrough && !!layer.onIntervalClick;
                const Tag = interactive ? 'button' : 'div';
                return (
                  <Tag
                    key={`${layer.id}:${interval.id}`}
                    type={interactive ? 'button' : undefined}
                    className={cn(
                      'absolute inset-x-0.5 overflow-hidden rounded-md',
                      styles.block,
                      styles.passthrough && 'pointer-events-none',
                      interactive && 'transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                    )}
                    style={{ top, height }}
                    onClick={interactive ? (e) => {
                      e.stopPropagation();
                      layer.onIntervalClick?.(interval);
                    } : undefined}
                    aria-label={interval.label}
                  >
                    {height >= 28 && (
                      layer.variant === 'group-class' ? (
                        <span className={cn('flex min-w-0 items-baseline gap-1 px-2 py-0.5 leading-tight text-left', styles.label)}>
                          <span className="shrink-0 text-[11px] font-semibold tabular-nums not-italic">
                            {format(interval.start, 'HH:mm')}
                          </span>
                          {interval.label && (
                            <span className="min-w-0 flex-1 truncate text-[11px] font-normal italic opacity-75">
                              {interval.label}
                            </span>
                          )}
                        </span>
                      ) : (
                        interval.label && (
                          <span className={cn('block truncate px-2 py-0.5 text-body-xs font-medium leading-tight text-left', styles.label)}>
                            {interval.label}
                          </span>
                        )
                      )
                    )}
                  </Tag>
                );
              })}

              {/* Events */}
              {laid.map(({ event: item, lane, lanes, overflow, hidden }) => {
                if (hidden) return null;
                const ev = item.ref;
                const startMins = ev.start.getHours() * 60 + ev.start.getMinutes();
                const endMins = ev.end.getHours() * 60 + ev.end.getMinutes();
                const top = startMins * pixelsPerMinute;
                const height = Math.max(20, (endMins - startMins) * pixelsPerMinute - 2);
                const width = `calc(${100 / lanes}% - 2px)`;
                const left = `calc(${(lane * 100) / lanes}% + 1px)`;
                const styles = statusStyles[ev.status];

                return (
                  <button
                    key={ev.id + (overflow ? '-overflow' : '')}
                    type="button"
                    className={cn(
                      'absolute overflow-hidden rounded-md border text-left',
                      'transition-colors hover:shadow-card',
                      styles.bg,
                      styles.border,
                      styles.text,
                      ev.status === 'cancelled' && 'opacity-60 line-through',
                    )}
                    style={{ top, height, left, width }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick?.(ev);
                    }}
                  >
                    <span className={cn('absolute left-0 top-0 h-full w-1', styles.stripe)} aria-hidden />
                    {(() => {
                      if (overflow) {
                        return (
                          <div className="px-2 py-1.5 pl-3 text-body-xs font-semibold">
                            +{overflow + 1} more
                          </div>
                        );
                      }
                      const compact = height < 36;
                      if (compact) {
                        return (
                          <div className="flex h-full w-full min-w-0 items-center gap-1.5 px-2 pl-3 leading-tight overflow-hidden">
                            <span className="text-[11px] font-semibold tabular-nums shrink-0">
                              {format(ev.start, 'HH:mm')}
                            </span>
                            <span className="block min-w-0 flex-1 truncate text-[11.5px] font-medium">
                              {ev.title}
                              {ev.subtitle && (
                                <span className="ml-1 font-normal italic opacity-70">· {ev.subtitle}</span>
                              )}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <div className="px-2 py-1.5 pl-3">
                          <div className="text-[11px] font-semibold tabular-nums leading-tight">
                            {format(ev.start, 'HH:mm')} – {format(ev.end, 'HH:mm')}
                          </div>
                          <div className="truncate text-[12.5px] font-medium leading-snug">{ev.title}</div>
                          {ev.subtitle && (
                            <div className="truncate text-[11px] font-normal italic opacity-75">
                              {ev.subtitle}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </button>
                );
              })}

              {/* Now indicator */}
              {showNow && (
                <>
                  <div
                    className="pointer-events-none absolute inset-x-0 z-10 h-[2px] bg-brand-500"
                    style={{ top: nowTop }}
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute z-10 -translate-x-full -translate-y-1/2 rounded-pill bg-brand-500 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white"
                    style={{ top: nowTop, left: 0 }}
                  >
                    Now
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* Loading skeleton overlay */}
        {loading && (
          <div className="col-span-full row-span-full flex flex-col gap-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-12 w-1/2" />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Column-header strip (above body) ───────────────────────────────────

function ColumnsHeader({
  columns,
  showAvatars,
  scrollRef,
}: {
  columns: Column[];
  showAvatars?: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={scrollRef}
      // Scrolled programmatically by the body; never by the user directly.
      className="overflow-hidden border-b border-line bg-surface"
    >
      <div
        className="grid"
        style={{ gridTemplateColumns: `60px repeat(${columns.length}, minmax(${COLUMN_MIN_WIDTH}px,1fr))` }}
      >
      <div className="sticky left-0 z-20 bg-surface" />
      {columns.map((col) => (
        <div
          key={col.id}
          className={cn(
            'flex items-center gap-2 border-r border-line px-3 py-2 last:border-r-0',
            col.isToday && 'bg-brand-50',
          )}
        >
          {showAvatars && (
            <Avatar size="sm">
              {col.avatarUrl ? (
                <AvatarImage src={col.avatarUrl} alt={col.label} />
              ) : (
                <AvatarInitials id={col.id} name={col.label} />
              )}
            </Avatar>
          )}
          <div className="min-w-0">
            <div className={cn('truncate text-body-sm font-semibold', col.isToday ? 'text-brand-700' : 'text-ink')}>
              {col.label}
            </div>
            {col.sublabel && <div className="truncate text-body-xs text-soft">{col.sublabel}</div>}
          </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Body: month ────────────────────────────────────────────────────────

interface MonthBodyProps {
  date: Date;
  events: CalendarEvent[];
  layers?: CalendarLayer[];
  weekStartsOn?: 0 | 1;
  onEventClick?: (e: CalendarEvent) => void;
  onSlotClick?: (slot: { start: Date; columnId: string }) => void;
}

const MAX_MONTH_CHIPS = 3;

const monthStripeByStatus: Record<EventStatus, string> = {
  confirmed:    'bg-status-booked',
  pending:      'bg-warn',
  'in-progress':'bg-brand-500',
  done:         'bg-success',
  cancelled:    'bg-line-strong',
  blocked:      'bg-status-blocked',
  'group-class':'bg-violet-500',
};

function MonthBody({ date, events, layers, weekStartsOn = 1, onEventClick, onSlotClick }: MonthBodyProps) {
  const { i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);
  const gridStart = startOfWeek(startOfMonth(date), { weekStartsOn });
  const gridEnd = endOfWeek(endOfMonth(date), { weekStartsOn });
  const days: Date[] = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);

  // Bucket events by yyyy-MM-dd of their start date.
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const e of events) {
      const k = format(e.start, 'yyyy-MM-dd');
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(e);
    }
    // Sort each day's events by start time.
    for (const arr of map.values()) arr.sort((a, b) => a.start.getTime() - b.start.getTime());
    return map;
  }, [events]);

  // Collect availability ranges per day from `available` / `override-available`
  // layers. Multiple distinct ranges in the same day render as separate chips.
  const availRangesByDay = useMemo(() => {
    const map = new Map<string, Array<{ start: Date; end: Date; label?: string }>>();
    if (!layers) return map;
    for (const layer of layers) {
      if (layer.variant !== 'available' && layer.variant !== 'override-available') continue;
      for (const i of layer.intervals) {
        const arr = map.get(i.columnId) ?? [];
        arr.push({ start: i.start, end: i.end, label: i.label });
        map.set(i.columnId, arr);
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start.getTime() - b.start.getTime());
    return map;
  }, [layers]);

  // Collect group-class ranges per day from `group-class` layer.
  const groupRangesByDay = useMemo(() => {
    const map = new Map<string, Array<{ start: Date; end: Date; label?: string }>>();
    if (!layers) return map;
    for (const layer of layers) {
      if (layer.variant !== 'group-class') continue;
      for (const i of layer.intervals) {
        const arr = map.get(i.columnId) ?? [];
        arr.push({ start: i.start, end: i.end, label: i.label });
        map.set(i.columnId, arr);
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => a.start.getTime() - b.start.getTime());
    return map;
  }, [layers]);

  const weekdayHeaders = useMemo(() => {
    const start = startOfWeek(date, { weekStartsOn });
    // 'EEEEEE' (short) not 'EEE' (abbreviated): date-fns ptBR's abbreviated
    // weekday is the long form ("segunda"); short abbreviates in every locale.
    return Array.from({ length: 7 }, (_, i) => format(addDays(start, i), 'EEEEEE', { locale }));
  }, [date, weekStartsOn, locale]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Weekday header strip */}
      <div className="grid shrink-0 grid-cols-7 border-b border-line bg-surface-2/40">
        {weekdayHeaders.map((wd) => (
          <div key={wd} className="px-2 py-2 text-mono-eyebrow text-soft">
            {wd}
          </div>
        ))}
      </div>

      {/* 5 or 6 week rows — height follows the real week count */}
      <div className="grid flex-1 grid-cols-7 auto-rows-[minmax(96px,1fr)]">
        {days.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const dayEvents = eventsByDay.get(key) ?? [];
          const inMonth = isSameMonth(d, date);
          const isCurrent = isSameDay(d, new Date());
          const visible = dayEvents.slice(0, MAX_MONTH_CHIPS);
          const hidden = dayEvents.length - visible.length;
          const availRanges = availRangesByDay.get(key) ?? [];
          const hasAvail = availRanges.length > 0;
          const groupRanges = groupRangesByDay.get(key) ?? [];

          return (
            <button
              key={key}
              type="button"
              onClick={() => onSlotClick?.({ start: startOfDay(d), columnId: key })}
              className={cn(
                'flex min-h-[96px] flex-col gap-1 border-b border-r border-line p-1.5 text-left transition-colors',
                'hover:bg-surface-2/60',
                !inMonth && 'bg-surface-2/30',
                isCurrent && 'bg-brand-50',
                hasAvail && !isCurrent && inMonth && 'bg-success-soft/40',
              )}
            >
              <div
                className={cn(
                  'inline-flex h-6 w-6 items-center justify-center self-start rounded-pill text-[12px] tabular-nums',
                  isCurrent && 'bg-brand-500 font-semibold text-white',
                  !isCurrent && inMonth && 'text-ink',
                  !isCurrent && !inMonth && 'text-text-muted',
                )}
              >
                {format(d, 'd')}
              </div>

              <div className="flex flex-col gap-0.5">
                {availRanges.map((r, idx) => (
                  <div
                    key={`avail-${idx}`}
                    className="flex items-center gap-1 rounded-sm bg-success-soft px-1.5 py-0.5 text-[11px] leading-tight text-success-text"
                  >
                    <span className="h-2 w-2 shrink-0 rounded-pill bg-success" aria-hidden />
                    <span className="truncate font-medium">
                      {format(r.start, 'H:mm')} - {format(r.end, 'H:mm')}
                      {r.label ? ` · ${r.label}` : ''}
                    </span>
                  </div>
                ))}
                {groupRanges.map((r, idx) => (
                  <div
                    key={`group-${idx}`}
                    className="flex items-center gap-1 rounded-sm bg-violet-50 px-1.5 py-0.5 text-[11px] leading-tight text-violet-700"
                  >
                    <span className="h-2 w-2 shrink-0 rounded-pill bg-violet-500" aria-hidden />
                    <span className="truncate">
                      <span className="font-semibold tabular-nums not-italic">{format(r.start, 'H:mm')}</span>
                      {r.label && (
                        <span className="ml-1 font-normal italic opacity-75">{r.label}</span>
                      )}
                    </span>
                  </div>
                ))}
                {visible.map((ev) => (
                  <div
                    key={ev.id}
                    className={cn(
                      'flex items-center gap-1 rounded-sm bg-surface px-1.5 py-0.5 text-[11px] leading-tight',
                      'border border-line truncate',
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick?.(ev);
                    }}
                  >
                    <span className={cn('h-2 w-2 shrink-0 rounded-pill', monthStripeByStatus[ev.status])} aria-hidden />
                    <span className="tabular-nums text-soft">{format(ev.start, 'HH:mm')}</span>
                    <span className="truncate text-ink">{ev.title}</span>
                  </div>
                ))}
                {hidden > 0 && (
                  <div className="px-1.5 text-[11px] font-medium text-soft">+{hidden} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────

export function Calendar({
  view,
  date,
  onViewChange,
  onDateChange,
  events,
  staff,
  workingHours = DEFAULT_HOURS,
  slotMinutes = 60,
  pixelsPerMinute = DEFAULT_PX_PER_MIN,
  weekStartsOn = 1,
  onEventClick,
  onSlotClick,
  layers,
  loading,
  emptyState,
  toolbar,
  hideViewTabs,
  hideHeader,
  bodyFill,
  className,
}: CalendarProps) {
  const { i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);
  const columnsHeaderRef = useRef<HTMLDivElement>(null);

  // Build columns for the column-based views.
  const columns: Column[] = useMemo(() => {
    if (view === 'day') {
      return [
        {
          id: format(date, 'yyyy-MM-dd'),
          label: format(date, 'EEEE', { locale }),
          sublabel: format(date, 'd MMMM', { locale }),
          isToday: isTodayDate(date),
        },
      ];
    }
    if (view === 'team') {
      const ids = (staff ?? []).map((s) => ({
        id: s.id,
        label: s.name,
        avatarUrl: s.avatarUrl,
        isToday: isTodayDate(date),
      }));
      return ids.length > 0
        ? ids
        : [
            {
              id: format(date, 'yyyy-MM-dd'),
              label: 'No staff',
              isToday: isTodayDate(date),
            },
          ];
    }
    if (view === 'week') {
      return daysInWeek(date, weekStartsOn).map((d) => ({
        id: format(d, 'yyyy-MM-dd'),
        label: format(d, 'EEEEEE', { locale }),
        sublabel: format(d, 'd'),
        isToday: isTodayDate(d),
      }));
    }
    return [];
  }, [view, date, staff, weekStartsOn, locale]);

  const dateForColumn = useMemo(() => {
    if (view === 'week') {
      const lookup = new Map(daysInWeek(date, weekStartsOn).map((d) => [format(d, 'yyyy-MM-dd'), d]));
      return (col: Column) => lookup.get(col.id) ?? date;
    }
    // day/team — single day
    return () => startOfDay(date);
  }, [view, date, weekStartsOn]);

  return (
    <div className={cn('flex h-full flex-col rounded-2xl border border-line bg-surface shadow-card', className)}>
      {!hideHeader && (
        <CalendarHeader
          view={view}
          date={date}
          onViewChange={onViewChange}
          onDateChange={onDateChange}
          events={events}
          toolbar={toolbar}
          hideViewTabs={hideViewTabs}
          weekStartsOn={weekStartsOn}
        />
      )}

      {(view === 'day' || view === 'team' || view === 'week') && (
        <>
          {(view === 'team' || view === 'week') && (
            <ColumnsHeader
              columns={columns}
              showAvatars={view === 'team'}
              scrollRef={columnsHeaderRef}
            />
          )}
          <ColumnsBody
            columns={columns}
            events={
              view === 'week'
                ? assignWeekColumns(events)
                : events.filter((e) => isSameDay(e.start, date))
            }
            layers={layers}
            workingHours={workingHours}
            slotMinutes={slotMinutes}
            pixelsPerMinute={pixelsPerMinute}
            showAvatars={view === 'team'}
            loading={loading}
            emptyState={emptyState}
            bodyFill={bodyFill}
            headerRef={columnsHeaderRef}
            onEventClick={onEventClick}
            onSlotClick={onSlotClick}
            dateForColumn={dateForColumn}
          />
        </>
      )}

      {view === 'month' && (
        <MonthBody
          date={date}
          events={events}
          layers={layers}
          weekStartsOn={weekStartsOn}
          onEventClick={onEventClick}
          onSlotClick={onSlotClick}
        />
      )}
    </div>
  );
}

/** Week view assigns columnId from the event's start date if not already set. */
function assignWeekColumns(events: CalendarEvent[]): CalendarEvent[] {
  return events.map((e) =>
    e.columnId ? e : { ...e, columnId: format(e.start, 'yyyy-MM-dd') },
  );
}

export type { CalendarEvent, CalendarView, EventStatus, Staff, WorkingHours, Column };
